import React, { useMemo } from 'react';

import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import dayjs from '~/timeInit';

import { Box, Paper, Stack, Typography, useTheme } from '@mui/material';

import { ConfigurationSeries, DailyCostPoint, buildDailyCostPoints } from './dailyCostPoints';
import { ScalePricingItem, TimelinePoint } from './departureInventory';
import { formatAmount } from './formatAmount';

interface Props {
  timeline: Array<TimelinePoint>;
  currency: string;
  snapshotDate?: number;
  items: Array<ScalePricingItem>;
  season?: string | null;
  showCosts?: boolean;
}

const AXIS_INK = '#898781';
const GRID = { light: '#e1e0d9', dark: '#2c2c2a' };

/** Rates run to eight figures in VND, which no axis can carry in full. */
const compact = new Intl.NumberFormat('en-AU', { notation: 'compact', maximumFractionDigits: 1 });

function TrendTooltip({
  active,
  payload,
  currency,
  series,
  colors,
  showCosts,
}: {
  active?: boolean;
  payload?: Array<{ payload: DailyCostPoint }>;
  currency: string;
  series: Array<ConfigurationSeries>;
  colors: Array<string>;
  showCosts: boolean;
}) {
  const point = payload?.[0]?.payload;
  if (!active || !point) return null;
  return (
    <Paper variant="outlined" sx={{ px: 1.5, py: 1 }}>
      <Typography variant="caption" color="text.secondary" display="block">
        {dayjs(point.time).format('D MMM YYYY')} · End of day
      </Typography>
      {series.map((configuration, index) => {
        const balance = point.balances[configuration.id];
        return (
          <Typography key={configuration.id} variant="body2" sx={{ color: colors[index % colors.length] }}>
            {configuration.label}:{' '}
            {showCosts ? `${formatAmount(balance.cost)} ${currency} · ${balance.pax} pax` : `${balance.pax} pax`}
          </Typography>
        );
      })}
    </Paper>
  );
}

/**
 * End-of-day supplier cost for each tour/package configuration pair.
 * Each step represents the closing balance after all movements on that local day.
 */
export default function CostPriceTrendChart({
  timeline,
  currency,
  snapshotDate,
  items,
  season,
  showCosts = true,
}: Props) {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  const { points: data, series } = useMemo(
    () => buildDailyCostPoints(timeline, items, currency, season),
    [timeline, items, currency, season],
  );
  const colors = [
    theme.palette.primary.main,
    theme.palette.secondary.main,
    theme.palette.success.main,
    theme.palette.warning.main,
    theme.palette.info.main,
    theme.palette.error.main,
  ];
  const dayTicks = useMemo(() => Array.from(new Set(data.map((point) => point.time))), [data]);

  if (data.length < 2) {
    return (
      <Typography variant="body2" color="text.secondary">
        Not enough movements to show a trend yet.
      </Typography>
    );
  }

  return (
    <Stack spacing={0.5}>
      <Box sx={{ width: '100%', height: 240 }}>
        <ResponsiveContainer>
          <LineChart data={data} margin={{ top: 8, right: 16, bottom: 4, left: 8 }}>
            <CartesianGrid stroke={isDark ? GRID.dark : GRID.light} vertical={false} />
            <XAxis
              dataKey="time"
              type="number"
              scale="time"
              domain={['dataMin', 'dataMax']}
              ticks={dayTicks}
              tickFormatter={(value) => dayjs(value).format('D MMM')}
              tick={{ fill: AXIS_INK, fontSize: 12 }}
              stroke={isDark ? GRID.dark : GRID.light}
            />
            <YAxis
              tickFormatter={(value) => (showCosts ? compact.format(value) : String(value))}
              tick={{ fill: AXIS_INK, fontSize: 12 }}
              stroke={isDark ? GRID.dark : GRID.light}
              width={56}
              domain={['auto', 'auto']}
            />
            <Tooltip
              content={<TrendTooltip currency={currency} series={series} colors={colors} showCosts={showCosts} />}
              cursor={{ stroke: AXIS_INK, strokeDasharray: '3 3' }}
            />
            <Legend />
            {series.map((configuration, index) => (
              <Line
                key={configuration.id}
                name={configuration.label}
                type="stepAfter"
                dataKey={`balances.${configuration.id}.${showCosts ? 'cost' : 'pax'}`}
                stroke={colors[index % colors.length]}
                strokeWidth={2}
                dot={false}
                activeDot={{ r: 6 }}
                isAnimationActive={false}
                connectNulls={false}
              />
            ))}
            {snapshotDate !== undefined && (
              <ReferenceLine
                x={dayjs(snapshotDate).startOf('day').valueOf()}
                stroke={theme.palette.text.primary}
                strokeDasharray="4 4"
              />
            )}
          </LineChart>
        </ResponsiveContainer>
      </Box>
    </Stack>
  );
}
